/**
 * Registro attività della società (V53), in sola lettura per l'Admin, e
 * della piattaforma (V57), per il Super Admin.
 *
 * Cancellazioni, modifiche a ruoli e accessi e azioni del gestore della
 * piattaforma, dal più recente. I filtri stanno nella query string, come
 * nella scheda giocatore; le righe oltre la prima pagina si aggiungono dal
 * fondo e ripartono da capo a ogni filtro. Nella società una riga su un
 * account ancora presente apre i suoi ruoli (V51); nella piattaforma c'è in
 * più il filtro e la colonna della società, che apre la sua scheda (V55).
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { OctagonXIcon } from 'lucide-react';
import {
  ETICHETTA_TIPO_REGISTRO,
  SENZA_SOCIETA,
  TIPI_REGISTRO,
  tipoDellAzione,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { CampoData } from '../comuni/CampoData.js';
import { Filtro } from '../comuni/FiltriVista.js';
import {
  type FiltriVista,
  type PaginaRegistro,
  type VoceRegistro,
  filtriDaSearch,
  fraseVoce,
  quando,
  queryRegistro,
} from './registro-attivita.js';
import { Nota } from '../comuni/Nota.js';

const TUTTI = '__tutti__';

export const RegistroPiattaforma = () => <Registro piattaforma />;

export function Registro({ piattaforma = false }: { piattaforma?: boolean }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const filtri = filtriDaSearch(search, piattaforma);
  const query = queryRegistro(filtri);
  const base = piattaforma ? '/piattaforma/registro' : '/amministrazione/registro';
  const { dati, errore } = useDati<PaginaRegistro>(`${base}${query}`);
  // Le pagine successive valgono solo per i filtri con cui sono state chieste.
  const [altre, setAltre] = useState<{
    query: string;
    voci: VoceRegistro[];
    prossimo: string | null;
  } | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [erroreAltre, setErroreAltre] = useState<string | null>(null);

  const imposta = (campo: keyof FiltriVista, valore: string) => {
    const prossimi: FiltriVista = { ...filtri, [campo]: valore || undefined };
    navigate(`${pathname}${queryRegistro(prossimi)}`, { replace: true });
  };

  // Cambiando filtro l'ultima pagina resta a schermo finché arriva la nuova:
  // senza, i filtri sparirebbero a ogni cambio e con loro il campo appena toccato.
  const [ultimi, setUltimi] = useState<PaginaRegistro | null>(null);
  if (dati && dati !== ultimi) setUltimi(dati);
  const mostrati = dati ?? ultimi;

  if (!mostrati || (errore && !dati))
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const aggiunte = altre?.query === query ? altre : null;
  const voci = [...mostrati.voci, ...(aggiunte?.voci ?? [])];
  const prossimo = aggiunte ? aggiunte.prossimo : mostrati.prossimo;
  const filtrato = Object.keys(filtri).length > 0;

  const caricaAltre = async () => {
    if (!prossimo) return;
    setInCorso(true);
    setErroreAltre(null);
    try {
      const pagina = await chiama<PaginaRegistro>(
        `${base}${queryRegistro({ ...filtri, dopo: prossimo })}`,
      );
      setAltre({
        query,
        voci: [...(aggiunte?.voci ?? []), ...pagina.voci],
        prossimo: pagina.prossimo,
      });
    } catch (e) {
      setErroreAltre(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Testata
        titolo="Registro attività"
        voce={piattaforma ? 'piattaforma-registro' : 'registro'}
      />
      <Corpo>
        <div className="flex flex-col gap-4">
          <Nota>
            {piattaforma
              ? 'Le attività di tutte le società, con chi le ha fatte.'
              : 'Cancellazioni, modifiche a ruoli e accessi e interventi del gestore della piattaforma, con chi li ha fatti.'}
          </Nota>

          <div className="flex flex-wrap items-center gap-3">
            {piattaforma && (
              <Filtro
                etichetta="Società"
                tutti={TUTTI}
                valore={filtri.societa ?? TUTTI}
                onCambia={(v) => imposta('societa', v === TUTTI ? '' : v)}
                opzioni={[
                  { valore: TUTTI, etichetta: 'Tutte' },
                  ...(mostrati.societa ?? []).map((s) => ({ valore: s.id, etichetta: s.nome })),
                  { valore: SENZA_SOCIETA, etichetta: 'Società non più presenti' },
                ]}
              />
            )}
            <Filtro
              etichetta="Autore"
              tutti={TUTTI}
              valore={filtri.autore ?? TUTTI}
              onCambia={(v) => imposta('autore', v === TUTTI ? '' : v)}
              opzioni={[
                { valore: TUTTI, etichetta: 'Tutti' },
                ...mostrati.autori.map((a) => ({ valore: a.id, etichetta: a.nome })),
              ]}
            />
            <Filtro
              etichetta="Tipo"
              tutti={TUTTI}
              valore={filtri.tipo ?? TUTTI}
              onCambia={(v) => imposta('tipo', v === TUTTI ? '' : v)}
              opzioni={[
                { valore: TUTTI, etichetta: 'Tutti' },
                ...TIPI_REGISTRO.map((t) => ({ valore: t, etichetta: ETICHETTA_TIPO_REGISTRO[t] })),
              ]}
            />
            <div className="flex min-w-28 flex-1 @xl:max-w-64">
              <CampoData
                id="filtro-da"
                etichetta="Dal"
                valore={filtri.da ?? ''}
                onCambia={(v) => imposta('da', v)}
                cancellabile
              />
            </div>
            <div className="flex min-w-28 flex-1 @xl:max-w-64">
              <CampoData
                id="filtro-a"
                etichetta="Al"
                valore={filtri.a ?? ''}
                onCambia={(v) => imposta('a', v)}
                min={filtri.da}
                cancellabile
              />
            </div>
          </div>

          {voci.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nessuna attività</EmptyTitle>
                <EmptyDescription>
                  {filtrato
                    ? 'Nessuna riga corrisponde ai filtri scelti.'
                    : 'Qui compariranno cancellazioni e modifiche a ruoli e accessi.'}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {/* Con la colonna della società, autore e momento stanno insieme:
                    quattro colonne non entrano accanto al menu. */}
                  <TableHead>{piattaforma ? 'Quando e chi' : 'Quando'}</TableHead>
                  {!piattaforma && <TableHead>Autore</TableHead>}
                  {piattaforma && <TableHead>Società</TableHead>}
                  <TableHead className="whitespace-normal">Attività</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {voci.map((v) => {
                  const tipo = tipoDellAzione(v.azione);
                  const frase = fraseVoce(v);
                  return (
                    <TableRow key={v.id}>
                      {piattaforma ? (
                        <TableCell className="align-top whitespace-normal">
                          <span className="block tabular-nums">{quando(v.creatoIl)}</span>
                          <span className="block text-xs text-muted-foreground">
                            {v.autore?.nome ?? 'Account non più presente'}
                          </span>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell className="align-top tabular-nums">
                            {quando(v.creatoIl)}
                          </TableCell>
                          <TableCell className="align-top">
                            {v.autore ? (
                              v.autore.nome
                            ) : (
                              <span className="text-muted-foreground">
                                Account non più presente
                              </span>
                            )}
                          </TableCell>
                        </>
                      )}
                      {piattaforma && (
                        <TableCell className="align-top whitespace-normal">
                          <CellaSocieta voce={v} />
                        </TableCell>
                      )}
                      <TableCell className="whitespace-normal">
                        <span className="flex flex-wrap items-center gap-2">
                          {tipo && <Badge variant="outline">{ETICHETTA_TIPO_REGISTRO[tipo]}</Badge>}
                          {!piattaforma &&
                          v.entita === 'account' &&
                          v.entitaPresente &&
                          v.entitaId ? (
                            <Link
                              to={`/amministrazione/account/${v.entitaId}/ruoli`}
                              className="hover:underline"
                            >
                              {frase}
                            </Link>
                          ) : (
                            <span>{frase}</span>
                          )}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {erroreAltre && (
            <Alert variant="destructive">
              <OctagonXIcon />
              <AlertTitle>Caricamento non riuscito</AlertTitle>
              <AlertDescription>{erroreAltre}</AlertDescription>
            </Alert>
          )}

          {prossimo && (
            <div className="flex justify-center">
              <PulsanteInvio
                type="button"
                variant="outline"
                inCorso={inCorso}
                onClick={() => void caricaAltre()}
              >
                Carica altre
              </PulsanteInvio>
            </div>
          )}
        </div>
      </Corpo>
    </>
  );
}

/** La società della riga, o il nome salvato nel dettaglio se non c'è più. */
function CellaSocieta({ voce }: { voce: VoceRegistro }) {
  if (voce.societa) {
    return (
      <Link to={`/piattaforma/societa/${voce.societa.id}`} className="hover:underline">
        {voce.societa.nome}
      </Link>
    );
  }
  const salvata = typeof voce.dettaglio.societa === 'string' ? voce.dettaglio.societa : null;
  return (
    <span className="text-muted-foreground">
      {salvata ? `${salvata} (non più presente)` : 'Società non più presente'}
    </span>
  );
}
